import cloneDeep from 'lodash/cloneDeep';
import { Injectable } from '@angular/core';
import { StateService } from 'flux-core';
import { IShapeSetItem, IShapeSetPayload } from './shape-set.types';

export interface IEmbeddedShapeSetSourceRef {
    setId: string;
    itemId: string;
    setVersion: number;
}

export interface IEmbeddedShapeOrigin extends IEmbeddedShapeSetSourceRef {
    matchedBy: 'runtime' | 'fingerprint';
}

/**
 * Runtime context manager for Plus Create origin tracking of embedded shape-set items.
 * This service stores only in-memory mappings and never persists shape-set source metadata.
 *
 * @author  sajeevah
 * @since   2026-02-25
 */
@Injectable()
export class ShapeSetPlusCreateContextService {

    protected shapeOriginByShapeId = new Map<string, IEmbeddedShapeSetSourceRef>();

    constructor(
        protected state: StateService<any, any>,
    ) {}

    /**
     * Registers runtime shape origin for a newly inserted shape.
     */
    public setShapeOrigin( shapeId: string, source: IEmbeddedShapeSetSourceRef ): void {
        if ( !shapeId || !source?.setId || !source?.itemId || typeof source.setVersion !== 'number' ) {
            return;
        }
        this.shapeOriginByShapeId.set( this.getScopedShapeKey( shapeId ), cloneDeep( source ));
    }

    /**
     * Removes runtime shape origin by shape id.
     */
    public removeShapeOrigin( shapeId: string ): void {
        if ( !shapeId ) {
            return;
        }
        this.shapeOriginByShapeId.delete( this.getScopedShapeKey( shapeId ));
    }

    /**
     * Clears all runtime mappings.
     */
    public clearOrigins(): void {
        this.shapeOriginByShapeId.clear();
    }

    /**
     * Resolves embedded shape-set origin for a shape using runtime mapping first,
     * then fallback fingerprint matching against current imported sets.
     */
    public resolveShapeOrigin( shape: any, sets: IShapeSetPayload[] ): IEmbeddedShapeOrigin {
        const runtimeOrigin = this.resolveRuntimeOrigin( shape, sets );
        if ( runtimeOrigin ) {
            return runtimeOrigin;
        }
        return this.resolveFingerprintOrigin( shape, sets );
    }

    /**
     * Returns a stable runtime revision key for imported sets.
     */
    public getRevisionKey( sets: IShapeSetPayload[] ): string {
        return (sets || [])
            .map( set => `${ set?.id || '' }:${ set?.version || 0 }` )
            .sort()
            .join( '|' );
    }

    /**
     * Resolves runtime mapping and validates it against imported set state.
     */
    protected resolveRuntimeOrigin( shape: any, sets: IShapeSetPayload[] ): IEmbeddedShapeOrigin {
        const shapeId = shape?.id;
        if ( !shapeId ) {
            return null;
        }
        const scopedKey = this.getScopedShapeKey( shapeId );
        const source = this.shapeOriginByShapeId.get( scopedKey );
        if ( !source ) {
            return null;
        }
        const set = (sets || []).find( current => current?.id === source.setId );
        if ( !set ) {
            this.shapeOriginByShapeId.delete( scopedKey );
            return null;
        }
        const hasItem = (set.items || []).some( item => item?.meta?.id === source.itemId );
        if ( !hasItem ) {
            this.shapeOriginByShapeId.delete( scopedKey );
            return null;
        }
        return {
            ...cloneDeep( source ),
            setVersion: set.version,
            matchedBy: 'runtime',
        };
    }

    /**
     * Resolves origin by strict unique fingerprint match on imported set items.
     */
    protected resolveFingerprintOrigin( shape: any, sets: IShapeSetPayload[] ): IEmbeddedShapeOrigin {
        const shapeKey = this.getFingerprintKeyForShape( shape );
        if ( !shapeKey ) {
            return null;
        }

        const matches: Array<{ setId: string; setVersion: number; itemId: string }> = [];
        (sets || []).forEach( set => {
            (set?.items || []).forEach( item => {
                if ( this.getFingerprintKeyForItem( item ) === shapeKey ) {
                    matches.push({
                        setId: set.id,
                        setVersion: set.version,
                        itemId: item?.meta?.id,
                    });
                }
            });
        });

        if ( matches.length !== 1 ) {
            return null;
        }
        const match = matches[0];
        if ( !match.setId || !match.itemId ) {
            return null;
        }
        return {
            setId: match.setId,
            itemId: match.itemId,
            setVersion: match.setVersion,
            matchedBy: 'fingerprint',
        };
    }

    /**
     * Builds a deterministic fingerprint key from a canvas shape model.
     */
    protected getFingerprintKeyForShape( shape: any ): string {
        if ( !shape?.defId || typeof shape?.version !== 'number' ) {
            return '';
        }
        const textStyle = this.extractTextStyle( shape );
        const drawCode = this.extractDrawCode( shape );
        const width = shape?.bounds?.width || (shape?.defaultBounds?.width && shape?.scaleX
            ? Math.abs( shape.defaultBounds.width * shape.scaleX ) : null );
        const height = shape?.bounds?.height || (shape?.defaultBounds?.height && shape?.scaleY
            ? Math.abs( shape.defaultBounds.height * shape.scaleY ) : null );
        return this.toStableString({
            defId: shape.defId,
            version: shape.version,
            style: shape?.style && typeof shape.style === 'object' ? shape.style : undefined,
            typeStyle: width && height ? { bounds: { width, height } } : undefined,
            scaleX: typeof shape?.scaleX === 'number' ? shape.scaleX : undefined,
            scaleY: typeof shape?.scaleY === 'number' ? shape.scaleY : undefined,
            angle: typeof shape?.angle === 'number' ? shape.angle : undefined,
            defaultBounds: shape?.defaultBounds && typeof shape.defaultBounds.width === 'number' &&
                typeof shape.defaultBounds.height === 'number'
                ? { width: shape.defaultBounds.width, height: shape.defaultBounds.height }
                : undefined,
            shapeContext: shape?.shapeContext,
            textStyle: textStyle && Object.keys( textStyle ).length > 0 ? textStyle : undefined,
            drawCode: drawCode || undefined,
            data: shape?.data && typeof shape.data === 'object' ? shape.data : undefined,
        });
    }

    /**
     * Builds a deterministic fingerprint key from a shape-set item.
     */
    protected getFingerprintKeyForItem( item: IShapeSetItem ): string {
        if ( !item?.defId || typeof item?.version !== 'number' ) {
            return '';
        }
        return this.toStableString({
            defId: item.defId,
            version: item.version,
            style: item.style,
            typeStyle: item.typeStyle,
            scaleX: item.scaleX,
            scaleY: item.scaleY,
            angle: item.angle,
            defaultBounds: item.defaultBounds,
            shapeContext: item.shapeContext,
            textStyle: item.textStyle,
            drawCode: item.drawCode,
            data: item.data,
        });
    }

    /**
     * Extracts normalized text style content from a canvas shape model.
     */
    protected extractTextStyle( shape: any ): Record<string, { content: Array<Record<string, any>> }> {
        const textStyle = shape?.textStyle;
        if ( textStyle && typeof textStyle === 'object' ) {
            const normalized = {};
            Object.keys( textStyle ).forEach( key => {
                const value = textStyle[key];
                if ( value && Array.isArray( value.content )) {
                    normalized[key] = { content: value.content };
                } else if ( Array.isArray( value )) {
                    normalized[key] = { content: value };
                }
            });
            if ( Object.keys( normalized ).length > 0 ) {
                return normalized;
            }
        }

        const texts = shape?.texts;
        if ( !texts || typeof texts !== 'object' ) {
            return null;
        }
        const normalized = {};
        Object.keys( texts ).forEach( textId => {
            const content = texts[textId]?.content;
            if ( Array.isArray( content )) {
                normalized[textId] = { content };
            }
        });
        return Object.keys( normalized ).length > 0 ? normalized : null;
    }

    /**
     * Extracts draw-code representation from shape model values.
     */
    protected extractDrawCode( shape: any ): IShapeSetItem['drawCode'] {
        if ( shape?.drawCode?.instructions ) {
            return cloneDeep( shape.drawCode );
        }
        if ( Array.isArray( shape?.instructions )) {
            return {
                instructions: cloneDeep( shape.instructions ),
                defaultWidth: shape?.defaultBounds?.width,
                defaultHeight: shape?.defaultBounds?.height,
                scaleX: shape?.scaleX,
                scaleY: shape?.scaleY,
                lineThickness: shape?.style?.lineThickness,
            };
        }
        return null;
    }

    /**
     * Converts values into deterministic stable strings for deep comparisons.
     */
    protected toStableString( value: any ): string {
        if ( value === null || value === undefined ) {
            return String( value );
        }
        if ( Array.isArray( value )) {
            return `[${ value.map( v => this.toStableString( v )).join( ',' ) }]`;
        }
        if ( typeof value === 'object' ) {
            return `{${ Object.keys( value ).sort().map( key => `${ key }:${ this.toStableString( value[key] )}` ).join( ',' ) }}`;
        }
        return JSON.stringify( value );
    }

    /**
     * Returns current-diagram scoped key for shape origin mapping.
     */
    protected getScopedShapeKey( shapeId: string ): string {
        const diagramId = this.state.get( 'CurrentDiagram' ) || '';
        return `${ diagramId }:${ shapeId }`;
    }
}
